import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../ConfirmModal';
import { MonoLabel, SectionCap, EditorialButton, Pill, EmptyState } from '../Editorial';
import { beaconOpened, beaconRowOnce } from '../../lib/recruitingBeacons';
import CollegePicker from '../CollegePicker';

/* Followed schools + their topic toggles, extracted from FollowingPage so the
   standalone /following route and the Recruit ▸ Following tab share one
   implementation.

   This panel keeps its own autocomplete rather than using
   useCollegeAutocomplete: that hook is keyed per row because Offers and Visits
   have many editable rows, whereas Following has a single "add a school"
   control. Forcing the keyed API here would add indirection for no reuse. */

// The six boolean topic/notification toggles on each followed school.
// Order + labels mirror the iOS Following screen.
const TOGGLES = [
  { key: 'news_enabled', label: 'News' },
  { key: 'follow_coach', label: 'Coaching staff' },
  { key: 'topic_recruiting', label: 'Recruiting & commits' },
  { key: 'topic_games', label: 'Games & results' },
  { key: 'topic_nil', label: 'NIL & program' },
  { key: 'topic_development', label: 'Player development' },
];

export default function FollowingPanel() {
  const { API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);

  const [collegeQuery, setCollegeQuery] = useState('');
  // Rows already beaconed this mount, so flipping several toggles on one
  // school counts as one engagement, not one per switch.
  const beaconedRows = useRef(new Set());

  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  useEffect(() => {
    fetchFollowing().finally(() => setLoading(false));
  }, []);

  const showToast = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  const fetchFollowing = async () => {
    try {
      const res = await axios.get(`${API_URL}/athletes/following`, getAxiosConfig());
      setFollowing(res.data || []);
    } catch (err) {
      console.error('Error fetching following:', err);
    }
  };

  // --- Toggle a single boolean field (optimistic, revert on error) ---
  const handleToggle = async (row, key) => {
    beaconRowOnce(beaconedRows, API_URL, 'following', row.id);
    const next = !row[key];
    setFollowing(prev => prev.map(r => r.id === row.id ? { ...r, [key]: next } : r));
    try {
      await axios.patch(`${API_URL}/athletes/following/${row.id}`, { [key]: next }, getAxiosConfig());
    } catch (err) {
      console.error('Error updating follow toggle:', err);
      // Revert
      setFollowing(prev => prev.map(r => r.id === row.id ? { ...r, [key]: !next } : r));
      showToast('Could not save change', 'error');
    }
  };

  const handleDelete = (row) => {
    setConfirmAction({
      title: 'Unfollow School',
      message: `Stop following ${row.college_name || 'this school'}?`,
      confirmLabel: 'Unfollow',
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await axios.delete(`${API_URL}/athletes/following/${row.id}`, getAxiosConfig());
          setFollowing(prev => prev.filter(r => r.id !== row.id));
          showToast('Unfollowed');
        } catch (err) {
          console.error('Error deleting follow:', err);
          showToast('Could not unfollow', 'error');
        }
      },
      onCancel: () => setConfirmAction(null),
    });
  };

  const handleCollegeSelect = async (college) => {
    setCollegeQuery('');

    // Avoid duplicate follows of the same school.
    if (following.some(r => r.unitid === college.u)) {
      showToast(`Already following ${college.n}`);
      return;
    }

    beaconOpened(API_URL, 'following');

    try {
      const res = await axios.post(`${API_URL}/athletes/following`, {
        unitid: college.u,
        college_name: college.n,
        college_city: college.c,
        college_state: college.s,
        news_enabled: true,
        follow_coach: true,
        topic_recruiting: false,
        topic_games: false,
        topic_nil: false,
        topic_development: false,
      }, getAxiosConfig());
      // Prefer the server row (carries id + any backend defaults).
      if (res.data && res.data.id) {
        setFollowing(prev => [...prev, res.data]);
      } else {
        fetchFollowing();
      }
      showToast(`Following ${college.n}`);
    } catch (err) {
      console.error('Error following school:', err);
      showToast('Could not follow school', 'error');
    }
  };

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      <SectionCap label="Following" />

      {/* + Follow a school — college autocomplete */}
      <div className="ed-card">
        <div className="form-field" style={{ position: 'relative', marginBottom: 0 }}>
          <label className="form-field__label">+ Follow a school</label>
          <CollegePicker
            value={collegeQuery}
            onInputChange={setCollegeQuery}
            onSelect={handleCollegeSelect}
            placeholder="Start typing a college name..."
          />
        </div>
      </div>

      {following.map((row) => {
        const meta = [row.division, row.coach_name, row.coach_role].filter(Boolean).join(' · ');
        return (
          <div className="ed-card" key={row.id}>
            <div className="ed-row ed-row--between" style={{ marginBottom: 8, gap: 12 }}>
              <div className="ed-row" style={{ gap: 12, alignItems: 'center', minWidth: 0 }}>
                {row.logo_url && (
                  <img
                    src={row.logo_url}
                    alt=""
                    style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div className="ed-card__title" style={{ fontWeight: 600 }}>{row.college_name}</div>
                  {meta && <MonoLabel>{meta.toUpperCase()}</MonoLabel>}
                </div>
              </div>
              <EditorialButton variant="ghost" onClick={() => handleDelete(row)} style={{ color: 'var(--ed-accent-red)', padding: '6px 12px', flexShrink: 0 }}>
                Unfollow
              </EditorialButton>
            </div>

            <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {TOGGLES.map(t => (
                <Pill
                  key={t.key}
                  as="button"
                  variant={row[t.key] ? 'active' : 'ghost'}
                  onClick={() => handleToggle(row, t.key)}
                  aria-pressed={!!row[t.key]}
                >
                  {t.label}
                </Pill>
              ))}
            </div>
          </div>
        );
      })}

      {!loading && following.length === 0 && (
        <EmptyState title="You're not following any schools yet." body="Use the search above to follow a school and pick what updates you want." />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </>
  );
}
